import type { DocumentStatus, DocumentType, OrganizationType } from '@hsl-ems/contracts';

const apiBase = `${import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000'}/api/v1`;

export type FileStatus =
  'UPLOADING' | 'PENDING_SCAN' | 'CLEAN' | 'QUARANTINED' | 'REJECTED' | 'DELETING' | 'DELETED';

export interface FileMetadata {
  id: string;
  originalName: string;
  declaredMime: string;
  detectedMime: string | null;
  status: FileStatus;
  scanResult: string | null;
  purpose: string;
  sortOrder: number;
  linkedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentFileLink {
  purpose: string;
  sortOrder: number;
  linkedAt: string;
  file: Omit<FileMetadata, 'purpose' | 'sortOrder' | 'linkedAt'>;
}

export interface DocumentRecord {
  id: string;
  documentNo: string;
  type: DocumentType;
  applicantUserId: string;
  operatingOrgId: string;
  status: DocumentStatus;
  requestedAmount: string;
  approvedAmount: string | null;
  payableAmount: string | null;
  description: string;
  version: number;
  submittedAt: string | null;
  approvedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  reimbursement: {
    title: string;
    bankAccount: BankAccount;
    items: Array<{
      id: string;
      categoryId: string;
      incurredOn: string;
      description: string;
      amount: string;
      city: string | null;
      remark: string | null;
      sortOrder: number;
    }>;
  } | null;
  reimbursementOffsets: LoanOffsetPlan[];
  loan: {
    purpose: string;
    plannedUseDate: string;
    plannedRepaymentDate: string;
    bankAccount: BankAccount;
  } | null;
  cashRepayment: {
    loanDocumentId: string;
    amount: string;
    note: string | null;
    collectionStatus: string | null;
    confirmedAmount: string | null;
    collectedAt: string | null;
    collectionMethod: string | null;
    referenceNo: string | null;
    collectionProofFileId: string | null;
    decisionByUserId: string | null;
    decisionAt: string | null;
    rejectionReason: string | null;
    version: number;
  } | null;
  corporatePayment: {
    categoryId: string;
    vendorId: string;
    vendorBankAccountId: string;
    purpose: string;
    expectedPaymentDate: string;
    referenceNo: string | null;
    vendor: { name: string; unifiedCreditCode: string | null };
    vendorBankAccount: VendorAccount;
  } | null;
  files: DocumentFileLink[];
}

export interface LoanOffsetPlan {
  id: string;
  offsetNo: string;
  loanDocumentId: string;
  amount: string;
  status: 'PLANNED' | 'POSTED' | 'CORRECTED';
  sortOrder: number;
  postedAt: string | null;
  version: number;
  loanDocument: {
    id: string;
    documentNo: string;
    requestedAmount: string;
    completedAt: string | null;
    loan: { plannedRepaymentDate: string } | null;
  };
}

export interface AvailableLoan {
  id: string;
  documentNo: string;
  requestedAmount: string;
  completedAt: string | null;
  availableBalance: string;
  loan: { plannedRepaymentDate: string } | null;
}

export interface OperatingOrganization {
  id: string;
  code: string;
  name: string;
  type: OrganizationType;
  isPrimary: boolean;
}

export interface ExpenseCategory {
  id: string;
  code: string;
  name: string;
  organizationId: string | null;
  requiresAttachment: boolean;
  version: number;
}

export interface BankAccount {
  id: string;
  holderName: string;
  bankName: string;
  accountMasked: string;
  isDefault?: boolean;
  version?: number;
}

export interface VendorAccount {
  id?: string;
  accountName: string;
  bankName: string;
  accountMasked: string;
  bankRoutingCode: string | null;
  version?: number;
}

export interface Vendor {
  id: string;
  organizationId: string;
  name: string;
  unifiedCreditCode: string | null;
  contactName: string | null;
  version: number;
  accounts: Array<VendorAccount & { id: string }>;
}

type JsonBody = Record<string, unknown> | unknown[];

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    credentials: 'include',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json', ...init?.headers },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string;
      code?: string;
    } | null;
    if (response.status === 401) throw new Error('登录已失效，请重新登录');
    if (response.status === 409) throw new Error('数据已被其他操作更新，请刷新后重试');
    throw new Error(body?.message ?? `请求失败（${response.status}）`);
  }
  return response.json() as Promise<T>;
}

function mutationHeaders(idempotencyKey?: string): Record<string, string> {
  return {
    'X-CSRF-Token': sessionStorage.getItem('hsl-ems-csrf') ?? '',
    'X-Request-Id': `web-${crypto.randomUUID()}`,
    ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
  };
}

const resourceFor: Record<DocumentType, string> = {
  EXPENSE_REIMBURSEMENT: 'reimbursements',
  LOAN: 'loans',
  CASH_REPAYMENT: 'cash-repayments',
  LOAN_OFFSET: 'loan-offsets',
  CORPORATE_PAYMENT: 'corporate-payments',
};

export const p2Api = {
  operatingOrganizations: () => api<OperatingOrganization[]>('/me/operating-organizations'),
  categories: () => api<ExpenseCategory[]>('/expense-categories'),
  bankAccounts: () => api<BankAccount[]>('/me/bank-accounts'),
  createBankAccount: (payload: { bankName: string; accountNumber: string; isDefault: boolean }) =>
    api<BankAccount>('/me/bank-accounts', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify(payload),
    }),
  vendors: () => api<Vendor[]>('/vendors'),
  list: (type: DocumentType) => api<DocumentRecord[]>(`/${resourceFor[type]}`),
  availableLoans: (operatingOrgId: string) =>
    api<AvailableLoan[]>(
      `/me/available-loans?operatingOrgId=${encodeURIComponent(operatingOrgId)}`,
    ),
  get: (type: DocumentType, id: string) => api<DocumentRecord>(`/${resourceFor[type]}/${id}`),
  create: (type: DocumentType, payload: JsonBody) => {
    const idempotencyKey = `web-${crypto.randomUUID()}`;
    return api<DocumentRecord>(`/${resourceFor[type]}`, {
      method: 'POST',
      headers: mutationHeaders(idempotencyKey),
      body: JSON.stringify(payload),
    });
  },
  update: (type: DocumentType, id: string, payload: JsonBody) =>
    api<DocumentRecord>(`/${resourceFor[type]}/${id}`, {
      method: 'PATCH',
      headers: mutationHeaders(),
      body: JSON.stringify(payload),
    }),
  void: (type: DocumentType, id: string, version: number) =>
    api<DocumentRecord>(`/${resourceFor[type]}/${id}/void?version=${version}`, {
      method: 'POST',
      headers: mutationHeaders(),
      body: '{}',
    }),
  createUploadIntent: (payload: {
    documentId: string;
    originalName: string;
    mimeType: string;
    sizeBytes: number;
    purpose: string;
  }) =>
    api<{
      id: string;
      uploadUrl: string;
      expiresAt: string;
      requiredHeaders: Record<string, string>;
    }>('/files/upload-intents', {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify(payload),
    }),
  completeUpload: (id: string, sha256: string) =>
    api<{ id: string; status: 'PENDING_SCAN' }>(`/files/${id}/complete`, {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({ sha256 }),
    }),
  file: (id: string) => api<FileMetadata>(`/files/${id}`),
  removeFile: (id: string, documentId: string, documentVersion: number) =>
    api<{ documentVersion: number; removedAt: string }>(`/files/${id}/remove`, {
      method: 'POST',
      headers: mutationHeaders(),
      body: JSON.stringify({ documentId, documentVersion }),
    }),
  downloadFile: (id: string) =>
    api<{ url: string; expiresAt: string }>(`/files/${id}/download-url`),
};

export async function sha256Hex(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function acceptedMime(file: File): string | null {
  const byExtension: Record<string, string> = {
    pdf: 'application/pdf',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    heic: 'image/heic',
    heif: 'image/heif',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  };
  const lowerName = file.name.toLowerCase();
  const parts = lowerName.split('.');
  const extension = parts.pop() ?? '';
  const stem = parts.join('.');
  if (/\.(exe|com|bat|cmd|scr|js|mjs|sh|jar|zip|rar|7z|docm|xlsm)$/.test(stem)) return null;
  const expected = byExtension[extension];
  if (!expected) return null;
  return !file.type || file.type === expected ? expected : null;
}
